import { useEffect } from 'react'

import { EMPTY_LIST } from '../../../../constants/common'
import { ScreenContainerRef } from '../../../../context'
import { isListViewTypeBoard } from '../../../../utils/listViewType'
import { FilterDivider } from '../../../FilterDivider'
import { PageActions } from '../../../Page'
import { Pagination } from '../../../Pagination'
import {
  getIsListingArchivable,
  LISTING_TABLE_SKELETON_ROWS,
} from '../../../RentalPortalListing'
import { SelectionToolbar } from '../../../SelectionToolbar'
import { Skeleton } from '../../../Skeleton'
import {
  getSelectedSize,
  getTableResetSortingButton,
  Table,
  TableConfigModal,
  TableControlBar,
  TableControlButtons,
  TableMassAction,
  TableViewRegimeControl,
} from '../../../Table'
import {
  getRentalPortalBoardNextSelection,
  getRentalPortalBoardSelectionState,
} from '../../RentalPortal.utils'
import { RentalPortalListingsBoard } from '../RentalPortalListingsBoard'
import { RentalPortalListingsTableRow } from '../RentalPortalListingsTableRow'

import { getEmptyItemActions } from './RentalPortalListingsTableLayout.utils'

import * as styles from './RentalPortalListingsTableLayout.module.css'

function RentalPortalListingsTableLayout({
  activeItem,
  arrowNavigationConfig,
  changeViewTypeButtonProps,
  emptyState = null,
  errorState = null,
  getListingItemActions,
  hasBodyError,
  hasTable,
  massActions = EMPTY_LIST,
  modal = null,
  setItemAction,
  setMassAction,
  tablePage,
  onChangeFocusedRow,
  onClickRow,
  onMouseDownRow,
  onShowMore,
}) {
  const {
    checkIsItemSelected,
    columns,
    entity,
    exclude,
    handleApply,
    handleCleanSelection,
    handleConfigOpen,
    handleExclude,
    handleInitializeSelection,
    handleResetOrdering,
    handleSelect,
    handleSelectAll,
    handleSelectAllPages,
    handleSetColumns,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    handleUnselectAllPages,
    isAllSelected,
    isConfigOpen,
    isLoading,
    isRemoveSelection,
    listings,
    listViewType,
    ordering,
    pageScrollWrapperRef,
    pagination,
    refreshButtonProps,
    requiredColumns,
    selectAllPages,
    selected,
    selectedIds,
    setConfigOpen,
    statistic,
  } = tablePage

  const isBoardRegime = isListViewTypeBoard(listViewType)

  useEffect(() => {
    handleCleanSelection()
  }, [isBoardRegime])

  const hasMassActions = massActions.length > 0

  const hasSelectionToolbar =
    !isBoardRegime && (selected.length > 0 || selectAllPages)

  const hasBoardSelectionCount = isBoardRegime && selected.length > 0

  const boardSelection = getRentalPortalBoardSelectionState({
    getIsSelectable: getIsListingArchivable,
    items: listings,
    selectedIds,
  })

  function handleSelectBoardGroup(groupItems, isChecked) {
    handleInitializeSelection(
      getRentalPortalBoardNextSelection({
        isChecked,
        items: groupItems,
        selected,
      })
    )
  }

  function handleSelectAllOnBoard() {
    handleSelectBoardGroup(
      boardSelection.selectableItems,
      !boardSelection.isAllSelected && !boardSelection.isPartlySelected
    )
  }

  return (
    <>
      {hasTable ? (
        <>
          <TableControlBar>
            {hasMassActions ? (
              <TableMassAction
                responsive
                actions={massActions}
                disabled={isBoardRegime && boardSelection.selectableCount === 0}
                isAllSelected={
                  isBoardRegime ? boardSelection.isAllSelected : isAllSelected
                }
                isLoading={isLoading}
                isRemoveSelection={
                  isBoardRegime
                    ? boardSelection.isPartlySelected
                    : isRemoveSelection
                }
                selectedSize={getSelectedSize({
                  exclude,
                  pagination,
                  selectAllPages,
                  selected,
                })}
                setMassAction={setMassAction}
                onSelectAll={
                  isBoardRegime ? handleSelectAllOnBoard : handleSelectAll
                }
              />
            ) : null}
            <PageActions className={styles.pageActions}>
              <TableControlButtons
                keepChangeViewTypeButtonOnNarrowScreen
                ChangeViewTypeButton={TableViewRegimeControl}
                DensityButton={null}
                ResetSortingButton={getTableResetSortingButton(ordering)}
                changeViewTypeButtonProps={changeViewTypeButtonProps}
                configButtonProps={{
                  disabled: isBoardRegime,
                  onClick: handleConfigOpen,
                }}
                isSkeleton={isLoading}
                refreshButtonProps={refreshButtonProps}
                resetSortingProps={{ onClick: handleResetOrdering }}
              />
              <FilterDivider />
              {isLoading ? (
                <Skeleton className={styles.paginationSkeleton} />
              ) : (
                <Pagination
                  responsive
                  disabled={isBoardRegime}
                  value={pagination}
                  onChange={handleSetPagination}
                  onChangeSize={handleSetSize}
                />
              )}
            </PageActions>
          </TableControlBar>
          {hasBodyError ? (
            errorState
          ) : isBoardRegime ? (
            <RentalPortalListingsBoard
              activeItem={activeItem}
              emptyState={emptyState}
              getActions={getListingItemActions || getEmptyItemActions}
              hasSelection={hasMassActions}
              isLoading={isLoading}
              items={listings}
              selectedIds={selectedIds}
              statistic={statistic}
              onCallAction={setItemAction}
              onClickCard={onClickRow}
              onSelectCard={handleSelect}
              onSelectColumn={handleSelectBoardGroup}
              onShowMore={onShowMore}
            />
          ) : listings.length === 0 && !isLoading ? (
            emptyState
          ) : (
            <Table
              TableRow={RentalPortalListingsTableRow}
              arrowNavigationConfig={arrowNavigationConfig}
              checkIsItemSelected={checkIsItemSelected}
              columns={columns}
              containerRef={pageScrollWrapperRef}
              headerRowProps={{
                ordering,
                onApply: handleApply,
                onSetColumns: handleSetColumns,
                onSetOrdering: handleSetOrdering,
              }}
              isLoading={isLoading}
              items={listings}
              rowProps={{
                activeItem,
                exclude,
                getActions: getListingItemActions || getEmptyItemActions,
                hideCheckbox: !hasMassActions,
                isSelectedAll: selectAllPages,
                selected,
                onCallAction: setItemAction,
                onClick: onClickRow,
                onMouseDown: onMouseDownRow,
                onSelect: selectAllPages ? handleExclude : handleSelect,
              }}
              skeletonRows={LISTING_TABLE_SKELETON_ROWS}
              onChangeFocusedRow={onChangeFocusedRow}
            />
          )}
        </>
      ) : (
        emptyState
      )}
      {hasSelectionToolbar ? (
        <SelectionToolbar
          allSelected={selectAllPages || selected.length === pagination.count}
          count={pagination.count}
          excludeCount={exclude.length}
          selected={selected.length}
          onClose={handleUnselectAllPages}
          onSelectAll={handleSelectAllPages}
        />
      ) : hasBoardSelectionCount ? (
        <SelectionToolbar
          selected={selected.length}
          onClose={handleCleanSelection}
        />
      ) : null}
      {isConfigOpen ? (
        <TableConfigModal
          columns={columns}
          entity={entity}
          requiredColumns={requiredColumns}
          setColumns={handleSetColumns}
          setConfigOpen={setConfigOpen}
          onApply={handleApply}
        />
      ) : null}
      <ScreenContainerRef value={null}>{modal}</ScreenContainerRef>
    </>
  )
}

export default RentalPortalListingsTableLayout
